import { LoginCallback, Security } from '@okta/okta-react'
import { OktaAuth, toRelativeUrl } from '@okta/okta-auth-js'
import { cacheManager, removeAppCookies, Static_Pages } from 'helpers/util-common'
import React from 'react'
import { Route } from 'react-router-dom'
import { useHistory } from 'react-router-dom'
import { IDENTITY_DELEGATION_FLAG, SESSION_STORAGE_KEYS, USER_INFO } from 'models/common.models'
import { get } from "lodash";
import { useDispatch } from 'react-redux'
import { setFeatureFlag } from 'common.actions'
import { restoreOktaSession } from './OktaSessionRestore'
import CustomLoader from 'shared-components/Loader'

// Flag For child application.
export let PROTACTED_HOME_PAGE = process.env.REACT_APP_MEETING_BASE_URL

// Okta Scopes
export const OKTA_SCOPES = ['openid', 'profile', 'email'];

// Setup Configuration for OktaAuth. 
export const OIDC = new OktaAuth({
	issuer: process.env.REACT_APP_OKTA_ISSUER,
	clientId: process.env.REACT_APP_OKTA_CLIENT_ID,
	redirectUri: process.env.REACT_APP_REDIRECT_URI,
	scopes: OKTA_SCOPES,
	pkce: true, // [true] provides a modern solution for protecting SPAs. 
	postLogoutRedirectUri: process.env.REACT_APP_LOGOUT_URI,
	disableHttpsCheck: window.location.hostname !== 'localhost' ? true : false, // [True] if working on https environment.
	tokenManager: {
		expireEarlySeconds: 60,
		autoRenew: true, // autorenew [true] auto update access token
		storage: 'memory',
		syncStorage: false,
	},
	storageManager: {
        token: {
            storageType: 'memory',
        },
        cache: {
            storageType: 'memory',
        },
        transaction: {
            storageType: 'memory',
        },
    },
	services: {
	  autoRenew: true,
	  autoRemove: true
	}
})

export const isStaticPageHandler = (location) => {
	const pathName = location ? location.pathname.toLowerCase() : window.location.pathname;
	const normalizedTargetUrl = pathName ? pathName.replace(/\/+$/, '') : "";
	if (Static_Pages.includes(normalizedTargetUrl)) {
		return normalizedTargetUrl
	}
	return false
}

export const handleLoggedIn = async (redirectURL = '') => {
	//Manage static page redirection
    let redirectCallbackURL = '';
	const isStaticPage = isStaticPageHandler();
	if (isStaticPage) {
		redirectCallbackURL = `?callbackUrl=${window.btoa(window.location.pathname + window.location.search)}`
	}
    if (redirectURL) {
        redirectCallbackURL += `?callbackUrl=${window.btoa(redirectURL)}`
    }
    const genratedRedirectURL = process.env.REACT_APP_LOGIN_PAGE_REQUEST + window.btoa(window.location.origin + redirectCallbackURL);
    window.location.assign(genratedRedirectURL);
}

// Logout Handler
export const logoutHandler = async () => {
    window.localStorage.setItem('isMemberStillLoginInAnotherTab', false);
    try {
		OIDC.tokenManager.clear();
		OIDC.signOut('/');
    } catch (error) {
        console.error(error);
    }
}

export const redirectUrlHandler = () => {
    const redirectUrl = sessionStorage.getItem("REDIRECT_URL");
    return redirectUrl || null;
}

export const setPageRedirectUrl = (pathname = window.location.pathname, redirectUrlKey) => {
    const currentPathname = pathname + window.location.search;
    sessionStorage.setItem(redirectUrlKey ? redirectUrlKey : SESSION_STORAGE_KEYS.REDIRECT_CALLBACK_URL, currentPathname);
};

export const verifySessionAndGetToken = (isLoginRequired = false) => {
  const callbackHandler = async (response) => {
    if (!response && isLoginRequired) {
      const callbackUrl = redirectUrlHandler();
      handleLoggedIn(
        callbackUrl
          ? handleLoggedIn(callbackUrl)
          : window.location.pathname + window.location.search,
      );
    } else {
      const idToken = await OIDC.tokenManager.get("idToken");
      if (!idToken) {
        restoreOktaSession({ oktaAuth: OIDC });
      } else if (idToken) {
        restoreOktaSession({ oktaAuth: OIDC, sessionOnly: true });
      }
    }
  };
  const oktaProps = {
    oktaAuth: OIDC,
    callback: callbackHandler,
  };
  restoreOktaSession(oktaProps);
};

export const oktaErrorLoginHandler = () => {
	removeAppCookies();
	const featureFlagData = window.localStorage.getItem("flagFeature");
	const staticPageUrl = isStaticPageHandler();
	window.localStorage.clear();
	if (staticPageUrl && featureFlagData) {
		window.localStorage.setItem("flagFeature", featureFlagData);
	}
}

export const getIdentityDelegation = () => {
	const identityDelegation = cacheManager.getItem(USER_INFO)
	  ? get(JSON.parse(cacheManager.getItem(USER_INFO)), IDENTITY_DELEGATION_FLAG)
	  : null;
	if (identityDelegation) {
	  return identityDelegation;
	}
	return null;
};

// TODO : set auth token header.
export const getAuthHeaderToken = async () => {
	const tokenInfo = await OIDC.tokenManager.get('accessToken');
	if (tokenInfo) {
		if (OIDC.tokenManager.hasExpired(tokenInfo)) {
			const renewAccessToken = await OIDC.token.renew(tokenInfo);
			return renewAccessToken ? renewAccessToken.accessToken : null;
		}
		return tokenInfo.accessToken;
	}
	return null;
}

// TODO : Render security component from common place.
export const BrowserRoutesProvider = ({ OIDC_CONFIG, ...props }) => {
	const history = useHistory()
	const dispatch = useDispatch();
	const hashValue = window.location.hash;
	const restoreOriginalUri = async (_oktaAuth, originalUri) => {
        history.replace(toRelativeUrl(originalUri || "/", window.location.origin));
    };

	 // https://github.com/okta/okta-auth-js/issues/1365#issuecomment-1387698694
	 React.useEffect(() => {
        (async () => {
            await OIDC.start();
		})();
		 
        return () => {
            (async () => {
                await OIDC.stop();
            })();
        }
    }, []);

	React.useEffect(() => {
        if(window.localStorage.getItem("flagFeature")) {
			dispatch(setFeatureFlag(true))
		}
    }, []);

	React.useEffect(() => {
	if (hashValue) {
		const sessionToken = window.location.hash.split('#')[1];
		if (sessionToken === 'IdentityDelegationRefreshToken') {
			cacheManager.clearSession();
			setPageRedirectUrl('/meeting');
			window.location.replace(window.location.origin);
		} else {
			const oktaProps = {
				oktaAuth: OIDC,
				sessionToken: sessionToken,
			};
			restoreOktaSession(oktaProps);
		}
	}
	}, [hashValue]);
	
	if (hashValue) {
    	return <CustomLoader />;
    }

	const handleAuth = () => {
		verifySessionAndGetToken(true);
	};

	return (
		<Security oktaAuth={OIDC_CONFIG} onAuthRequired={handleAuth} restoreOriginalUri={restoreOriginalUri}>
			<Route path={`${process.env.REACT_APP_MEETING_BASE_URL}/auth`} component={LoginCallback} />
				{props.children}
		</Security>
	)
}

export default {
	OIDC,
	BrowserRoutesProvider,
	getAuthHeaderToken
}